import { PropertiesPanel } from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import { isRealMesh, isTryOnFineTuningNode, ObjectConfigHelpers, ObjectNode, useObjectPropertiesCreator } from '../objectEditorConfig';
import { liveTransform } from '../transformSpace';
import InspectorShell from '../inspectorShell';
import { Dialog, useDialogManager } from '../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';

// L'involucro — contenitore, titolo e il `createGlobalStyle` che inchiodava gli input a 80px
// con `!important` — è passato a `InspectorShell`, uno per tutti i generi. Qui resta ciò che
// è solo di un oggetto: il suo stato locale e le sue proprietà.

export interface ObjectOverlayProps {
    node: ObjectNode;
    sceneEditor: any;
    onPropertiesUpdate: (updates: Partial<ObjectNode>) => void;
    onPropertyChanged?: (property: string, value: any) => void;
    /** Porta la colonna di sinistra sui materiali e accende quello di questo oggetto. */
    onRevealMaterial?: (materialId: string) => void;
    /** «Dove sta?»: fa lampeggiare la riga di questo oggetto nell'albero. */
    onReveal?: () => void;
}

const ObjectOverlayComponentNew: FC<ObjectOverlayProps> = ({ node, sceneEditor, onPropertiesUpdate, onPropertyChanged, onRevealMaterial, onReveal }) => {
    // Stato interno per mantenere i valori durante l'editing
    const [localState, setLocalState] = useState<ObjectNode>({
        ...node,
        position: [...node.position] as [number, number, number],
        rotation: [...node.rotation] as [number, number, number],
        scaling: [...node.scaling] as [number, number, number]
    });

    const isVTOEnabledInScene = sceneEditor?.getTryOnEnabled?.() ?? false;

    // L'AR SI ACCENDE PER LA SCENA, non per la mesh: sta nelle impostazioni, Behaviours ›
    // Augmented Reality, e da lì scende nella configurazione generale. Spenta lì, «Show in AR» su
    // una singola mesh scriverebbe un valore che nessuno legge.
    const isAREnabledInScene = sceneEditor?.getSceneConfig?.()?.get?.('general')?.get?.('arEnabled') ?? false;

    // LA CATENA NASCE COME LA TROVA. Accesa per forza, il primo numero che si batte
    // schiaccerebbe una scala non uniforme su un valore solo — cioè distruggerebbe un dato che
    // qualcuno aveva impostato apposta. Spenta per forza, il caso comune (i tre assi uguali)
    // chiederebbe tre battute invece di una. Quindi: legata se lo è già.
    const [scaleLinked, setScaleLinked] = useState(() => node.scaling[0] === node.scaling[1] && node.scaling[1] === node.scaling[2]);
    const currentNodeIdRef = useRef(node.id);

    // Per nodi root, controlla se è una mesh padre e leggi i finetuning settings
    const [fineTuningSettings, setFineTuningSettings] = useState<any>(undefined);

    // Verifica se è un nodo root o transform basandosi sui metadata
    const isRootNode = node.metadata?.isRootNode === true;
    const isTransformNode = node.metadata?.isTransformNode === true;

    // I SETTING DEL FINETUNING STANNO DOVE IL TRY-ON LI SCRIVE, cioè sulla mesh che il motore
    // riconosce come sua: si leggono di lì e da nessun altro nodo, perché altrove non ce ne sono
    // che valgano.
    useEffect(() => {
        const mesh = node.data;
        const isTryOnMesh = isTryOnFineTuningNode(node, sceneEditor);

        setFineTuningSettings(isTryOnMesh && sceneEditor ? (sceneEditor.getMeshFineTuningData?.(mesh) ?? undefined) : undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node.data, sceneEditor]);

    // LO STATO INTERNO SERVE A NON PERDERE QUELLO CHE SI STA SCRIVENDO: un campo riscritto da
    // fuori mentre ci si digita dentro è inservibile. Ma si risincronizzava SOLO cambiando riga,
    // e così tutto ciò che cambiava da fuori restava fuori — rinominare dal «…» dell'albero
    // cambiava il nome nell'elenco e non qui, e il nome nuovo si vedeva solo chiudendo e
    // riaprendo il pannello.
    //
    // Il confronto giusto è con l'ULTIMO NODO RICEVUTO, non con quello che si vede a schermo: si
    // adotta ciò che è cambiato davvero là fuori, e un campo che da fuori non ha toccato nessuno
    // resta com'è, con dentro quello che l'utente sta scrivendo.
    const lastReceivedNode = useRef(node);

    useEffect(() => {
        const previous = lastReceivedNode.current;
        lastReceivedNode.current = node;

        // Riga nuova: si riparte da capo, non c'è niente da conservare.
        if (currentNodeIdRef.current !== node.id) {
            currentNodeIdRef.current = node.id;

            setLocalState({
                ...node,
                position: [...node.position] as [number, number, number],
                rotation: [...node.rotation] as [number, number, number],
                scaling: [...node.scaling] as [number, number, number]
            });
            return;
        }

        // Le terne di numeri arrivano in array nuovi a ogni ricostruzione dell'albero: se non si
        // guarda dentro, «cambiato» risulta sempre e il pannello si riscriverebbe da solo.
        const sameValue = (a: any, b: any): boolean =>
            Array.isArray(a) && Array.isArray(b) ? a.length === b.length && a.every((item, index) => item === b[index]) : a === b;

        const arrived = (Object.keys(node) as (keyof ObjectNode)[]).filter((key) => typeof node[key] !== 'function' && !sameValue(previous[key], node[key]));

        if (!arrived.length) return;

        setLocalState((prev: ObjectNode) => {
            const next: any = { ...prev };
            arrived.forEach((key) => (next[key] = node[key]));
            return next as ObjectNode;
        });
    }, [node]);

    // IL GIZMO NON PASSA DI QUI, E NESSUNO AVVISA. Trascinando l'oggetto in scena i numeri di questo
    // pannello restavano quelli di quando l'hai aperto: il motore non emette niente sulle trasformate
    // mentre trascini, e l'elemento consegnato all'Inspector è sempre lo STESSO — React non ha motivo
    // di ridisegnarlo, e infatti i valori si aggiornavano solo chiudendo e riaprendo il pannello.
    // Restava una strada sola: guardare la mesh ogni tanto. Sono nove numeri, e solo mentre un
    // pannello è aperto.
    //
    // SI CONFRONTA CON L'ULTIMA LETTURA, non con quello che si vede: ciò che stai scrivendo in un
    // campo è già finito sulla mesh, quindi per questo controllo la mesh «non è cambiata» e il campo
    // resta tuo.
    const lastLive = useRef<ReturnType<typeof liveTransform> | null>(null);

    useEffect(() => {
        const mesh = node.data;
        if (!mesh) return;

        lastLive.current = liveTransform(mesh);

        const same = (a: [number, number, number], b: [number, number, number]) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2];

        const interval = setInterval(() => {
            const live = liveTransform(mesh);
            const previous = lastLive.current;

            if (previous && same(previous.position, live.position) && same(previous.rotation, live.rotation) && same(previous.scaling, live.scaling)) return;

            lastLive.current = live;
            setLocalState((prev) => ({ ...prev, ...live }));
        }, 150);

        return () => clearInterval(interval);
    }, [node.data]);

    // Helper per aggiornare sia lo stato locale che quello esterno
    const updateProperty = useCallback(
        (updates: Partial<ObjectNode>) => {
            // Validazione speciale per il nome
            if ('name' in updates && updates.name !== undefined) {
                updates.name = (updates.name || '').trim();
            }

            // Aggiorna lo stato locale immediatamente
            setLocalState((prev: ObjectNode) => ({ ...prev, ...updates }));

            // Aggiorna lo stato esterno
            onPropertiesUpdate(updates);
        },
        [onPropertiesUpdate]
    );

    // APRIRE IL FINETUNING È IL GIRO DI SEMPRE, e per un po' non lo è stato. Il pannello del
    // finetuning non è una finestra che questo componente apre: lo disegna la vista del palco
    // (`sceneEditorView`) quando trova due cose — `showFineTuning` acceso e il modello esportato
    // dentro `selectedProviderFineTuningData`. Qui si preparano quelle due cose, e basta.
    //
    // ERA RIMASTO A METÀ: il riscritto distingueva tre provider (DeepAR, Banuba/Mazing, classico)
    // e in tutti e tre i rami finiva in un `TODO`, quindi il modello esportato non arrivava mai a
    // destinazione e il pannello non aveva di che comparire. Per le scarpe — DeepAR — non si
    // accendeva nemmeno `showFineTuning`: il pulsante non faceva proprio niente.
    //
    // TORNA IDENTICO A PRIMA DELLE MODIFICHE GRAFICHE (utente, 2026-08-27): una strada sola per
    // tutti i provider — è il componente del finetuning a sapere che cosa fare col suo — e le
    // impostazioni VTO mancanti si dicono in console, non si sbarra la porta con un `alert`.
    const handleVirtualTryonFinetuning = useCallback(() => {
        const mesh = node.data; // La mesh di Babylon dal nodo
        if (!mesh) {
            console.error('Mesh not available in node data');
            return;
        }

        try {
            // Rimuovi tutti i listener esistenti
            sceneEditor.removeAllFineTuningSettingListeners?.();

            // Abilita il fine tuning
            sceneEditor.showFineTuning = true;

            // Senza impostazioni VTO il finetuning può non avere di che lavorare: si dice, non si
            // blocca — la configurazione può arrivare da più parti e non è questo il posto in cui
            // stabilirlo.
            const tryOnSettings = sceneEditor.getTryOnSettings?.();
            if (!tryOnSettings || !tryOnSettings.type) {
                console.warn('No VTO settings found. Fine tuning may not work without VTO configuration.');
            }

            // Se è un nodo root con settings esistenti, usali come punto di partenza
            const initialSettings: any = isRootNode && fineTuningSettings ? fineTuningSettings : undefined;

            // Aggiungi listener per i cambiamenti delle impostazioni
            sceneEditor.addFineTuningSettingListener?.((settings: any) => {
                sceneEditor.setMeshFineTuningData?.(mesh, settings);
                sceneEditor.setProviderFineTuningData?.({ settings });

                // Aggiorna lo stato locale dei settings
                setFineTuningSettings(settings);
            });

            // Esporta la mesh in formato GLB e consegnala al finetuning: è questo che lo fa
            // comparire.
            sceneEditor
                .exportMeshToGLBBlob?.(mesh.id)
                .then((blob: Blob) => {
                    if (!blob) {
                        console.error('Failed to export mesh to blob');
                        return;
                    }

                    const currentSettings = initialSettings || (sceneEditor.getMeshFineTuningData?.(mesh) ?? undefined);

                    sceneEditor.setProviderFineTuningData?.({ mesh: blob, settings: currentSettings });
                    sceneEditor.fineTuningModelLoaded = true;

                    // Notifica i cambiamenti
                    if (onPropertyChanged) {
                        onPropertyChanged('fineTuningModelLoaded', true);
                        onPropertyChanged('showFineTuning', true);
                    }
                })
                .catch((error: any) => {
                    console.error('Error exporting mesh for finetuning:', error);
                });
        } catch (error) {
            console.error('Error starting finetuning:', error);
        }
    }, [node, sceneEditor, isRootNode, fineTuningSettings, onPropertyChanged]);

    const handleResetFinetuningSettings = useCallback(() => {
        const mesh = node.data; // La mesh di Babylon dal nodo
        if (!mesh) {
            console.error('Mesh not available in node data');
            return;
        }

        try {
            // Reset dei dati del provider
            sceneEditor.resetProviderFineTuningData?.();

            // Reset dei metadata della mesh
            if (mesh.metadata && mesh.metadata.tryOnFineTuningSettings) {
                mesh.metadata.tryOnFineTuningSettings = undefined;
            }

            // Reset dello stato locale dei settings
            setFineTuningSettings(undefined);

            // Notifica il cambiamento
            if (onPropertyChanged) {
                onPropertyChanged('tryOnFineTuningSettings', undefined);
            }
        } catch (error) {
            console.error('Error resetting finetuning settings:', error);
        }
    }, [node, sceneEditor, onPropertyChanged]);

    // CREARE UN MATERIALE DALLO SLOT VUOL DIRE DUE COSE, e la seconda è quella per cui lo si è
    // aperto: crearlo nella scena, e METTERLO QUI. Un materiale nuovo che nasce nella libreria e
    // lascia la mesh come prima obbligherebbe a cercarlo e assegnarlo a mano — cioè a rifare a
    // mano il gesto che si era appena chiesto.
    //
    // IL NOME SI CHIEDE PRIMA, con la stessa domanda che fa la libreria dei materiali: due modi di
    // battezzare la stessa cosa sarebbero due cose da tenere uguali.
    const { showDialog, closeDialog } = useDialogManager();

    const createMaterial = !sceneEditor?.createMaterial
        ? undefined
        : () => {
              const form = { name: '' };

              showDialog(
                  'new-material-from-slot',
                  <Dialog
                      title={T._('New material', 'Admin')}
                      buttons={[
                          {
                              label: T._('OK', 'Admin'),
                              onClick: () => {
                                  const name = form.name.trim();
                                  if (!name) return;

                                  const created = sceneEditor.createMaterial(name, 'PBR');
                                  closeDialog('new-material-from-slot');
                                  if (!created) return;

                                  updateProperty({ selectedMaterial: { value: created.id ?? created.name, label: created.name ?? name } });
                              }
                          }
                      ]}
                  >
                      <FormControl label={T._('Material name', 'Admin')}>
                          <Input
                              autoFocus
                              fullWidth
                              placeholder={T._('type material name...', 'Admin')}
                              onChange={(event) => (form.name = event.target.value)}
                          />
                      </FormControl>
                  </Dialog>
              );
          };

    // Crea gli helpers per il PropertiesPanel
    const helpers: ObjectConfigHelpers = {
        sceneEditor,
        updateProperty,
        scaleLinked,
        setScaleLinked,
        onPropertyChanged,
        isVTOEnabledInScene,
        isAREnabledInScene,
        fineTuningSettings,
        handleVirtualTryonFinetuning,
        handleResetFinetuningSettings,
        revealMaterial: onRevealMaterial,
        createMaterial
    };

    const objectPropertiesCreator = useObjectPropertiesCreator();

    if (!node) return null;

    // Com'è fatto, in due parole: il genere del nodo lo dice già la marca, qui va la
    // sostanza — se ha geometria, quanta.
    //
    // A SCHERMO UNA MESH SI CHIAMA «OBJECT». `mesh` resta il nome del dato — è la parola che
    // l'esportatore glTF usa — ma quella che si legge è la parola dell'elenco in cui la si
    // trova, e quell'elenco si chiama Objects. La marca sopra il nome dice già «Object»,
    // quindi qui la ripeteremmo: restano i due casi che l'elenco non dice, la radice e il
    // nodo che raggruppa e basta — che nella scena è un **Empty**.
    // NASCOSTO LO DICE LA MESH, ed è lo stesso dato che accende l’occhio nella riga: un pannello che
    // non dicesse niente lascerebbe chi guarda a chiedersi perché nel palco non si vede niente.
    const isHidden = node.data ? node.data.isVisible === false || node.data.isEnabled?.() === false : false;
    const isTryOnMesh = isVTOEnabledInScene && isTryOnFineTuningNode(node, sceneEditor);

    const vertices = typeof node.data?.getTotalVertices === 'function' ? node.data.getTotalVertices() : undefined;

    // I TRIANGOLI SONO UNA DIVISIONE, non un dato in piu da chiedere a nessuno: gli indici di una
    // mesh vanno a tre a tre. E dei due numeri e quello che dice quanto pesa davvero il modello,
    // quindi si legge per primo.
    const indices = typeof node.data?.getTotalIndices === 'function' ? node.data.getTotalIndices() : undefined;
    const triangles = indices ? Math.round(indices / 3) : undefined;

    // LA SPECIE LA DICE LA MARCA, non le specifiche: un Empty adesso ha la sua pastiglia, e
    // scriverne il nome anche qui sarebbe la stessa parola due volte a due centimetri di distanza.
    // Resta «Root», che non è una specie ma un posto — la cima del modello.
    const specs = [
        isRootNode ? T._('Root', 'Admin') : null,
        triangles ? `${triangles.toLocaleString()} ${T._('triangles', 'Admin')}` : null,
        vertices ? `${vertices.toLocaleString()} ${T._('vertices', 'Admin')}` : null
    ]
        .filter(Boolean)
        .join(' · ');

    // «Mostramelo» non c'è: il motore sa inquadrare la scena intera (`adjustCamera`), non un
    // oggetto solo. Registro dei punti non supportati, riga 23.
    return (
        <InspectorShell
            kind={isRealMesh(node) ? 'object' : 'empty'}
            name={localState.name}
            specs={specs}
            // DUE COSE CHE SI LEGGONO PRIMA DEL NOME: che questo nodo è nascosto, e che è QUELLO su cui
            // il try-on lavora. La seconda serve perché il finetuning vale su una mesh sola e
            // dall'albero non si distingue: senza la pastiglia, chi apre il pannello di un altro nodo
            // non sa se il capitolo manchi per un errore o perché quello non è il posto giusto.
            state={[isHidden ? T._('Hidden', 'Admin') : '', isTryOnMesh ? T._('VTO', 'Admin') : ''].filter(Boolean)}
            onRename={(name) => updateProperty({ name })}
            onReveal={onReveal}
        >
            {/* Niente gruppo che ripete il nome: la testata lo dice già, e un accordion in
                più fra il soggetto e le sue proprietà è solo un clic in più. */}
            <PropertiesPanel properties={objectPropertiesCreator(localState, helpers)} />
        </InspectorShell>
    );
};

export default ObjectOverlayComponentNew;
